import React, { useContext, useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { ClimaOptionsContext } from '..'
import { Checkbox, Form } from 'semantic-ui-react'
import { OptionContent } from '../style'
import ClimaCheckService from '~/api/services/climaCheck'
import { useParams } from 'react-router-dom'
import { formatNumber, handleErrors } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'
import LocationTableTooltip from './LocationTableTooltip'

const fieldNames = [
  'grassSilage.locationFavorability',
  'grassSilage.fertilizationType',
  'grassSilage.cuttingFrequency',
  'grassSilage.freshMassYield',
  'grassSilage.dryMatter',
  'grassSilage.freshMassYield'
]

const FirstStep:React.FC = () => {
  const { control, setValue, clearErrors } = useFormContext()
  const { id } = useParams()

  const {
    locationFavorabilityTypes,
    fertilizationTypes,
    cuttingFrequencies
  } = useContext(ClimaOptionsContext)

  const [isLoadingYield, setIsLoadingYield] = useState(false)

  const [
    watchLocation,
    watchFertilisation,
    watchCuttingFreq,
    watchMassYield
  ] = useWatch({ control, name: fieldNames })

  useEffect(() => {
    if(watchLocation && watchFertilisation && watchCuttingFreq) {
      fetchYieldData()
    }
  }, [watchLocation, watchFertilisation, watchCuttingFreq])

  async function fetchYieldData() {
    setIsLoadingYield(true)
    try {
      const response = await ClimaCheckService.getGrassSilagePredefinedData(id, {
        locationFavorability: watchLocation,
        fertilizationType: watchFertilisation,
        cuttingFrequency: watchCuttingFreq
      })
      setValue('grassSilage.freshMassYield', formatNumber(response.data.freshMassYield, 0))
      clearErrors('grassSilage.freshMassYield')
    } catch(error) {
      handleErrors(error)
    }
    setIsLoadingYield(false)
  }

  return <>
    <OptionContent>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{'Standort'}</div>
            <InfoPopup
              content={<LocationTableTooltip/>}
              position={'top center'}
              hoverable={true}
              backgroundTransparent
              styleIcon={{ fontSize: '1.2em' }}
            />
          </label>}
          control={control}
          onChange={(e, data) => {
            setValue('grassSilage.locationFavorability', data.value)
            clearErrors('grassSilage.locationFavorability')
          }}
          placeholder='Anzahl Standort'
          name='grassSilage.locationFavorability'
          options={locationFavorabilityTypes.map(type => ({ value: type.key, text: type.label }))}
          noErrorMessage
          withoutAsterisk
        />
        <Controller
          name='grassSilage.fertilizationType'
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)} id={'grassSilage.fertilizationType'}>
              <div style={{ display: 'flex', gap: 20, marginTop: 32 }}>
                {fertilizationTypes.map(fertType => {
                  return <Checkbox
                    radio
                    key={fertType.key}
                    label={fertType.label}
                    checked={watchFertilisation === fertType.key}
                    onClick={() => {
                      setValue('grassSilage.fertilizationType', fertType.key)
                      clearErrors('grassSilage.fertilizationType')
                    }}
                  />
                })}
              </div>
            </Form.Field>
          )}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          label={'Schnitthäufigkeit'}
          control={control}
          placeholder='Anzahl Schnitthäufigkeit'
          name='grassSilage.cuttingFrequency'
          onChange={(e, data) => {
            setValue('grassSilage.cuttingFrequency', data.value)
            clearErrors('grassSilage.cuttingFrequency')
          }}
          options={cuttingFrequencies.map(type => ({ value: type.key, text: type.label }))}
          noErrorMessage
          withoutAsterisk
        />
        <FormFields.Input
          label={'Wie ist der Trockensubstanz-Gehalt der Grassilage?'}
          name='grassSilage.dryMatter'
          suffix=' % TM'
          placeholder='in % TM'
          noErrorMessage
          withoutAsterisk
          onChange={(event) => {
            setValue('grassSilage.dryMatter', event?.target?.value.replace('% TM', ''))
            clearErrors('grassSilage.dryMatter')
          }}
          control={control}
          typeInput='controled'
        />
      </Form.Group>
      <Form.Field>
        <label style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          {'Wie hoch ist der Frischmasse-Ertrag der Grassilage nach Abzug der Ernteverluste?'}
          <InfoPopup
            content={'Dieser Wert wird aus Ihren vorherigen Angaben automatisch auf Basis von Standardkalkulationen ermittelt und ist nicht änderbar'}
            position={'top center'}
            styleIcon={{ fontSize: '1.2em' }}
          />
        </label>
        <div>{`${formatNumber(watchMassYield || 0, 2)} t FM/ha`}</div>
      </Form.Field>
    </OptionContent>
  </>
}
export default FirstStep